import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Papa from "papaparse";
import membersService from "../features/members/membersService";

/**
 * BSA member CSV import: completes player profiles.
 *
 * Flow: pick the district the export belongs to, load the CSV, preview the
 * parsed rows (club resolutions included) without writing anything, then
 * import. Fill-only: existing players only gain fields that are empty.
 * Race appears in this preview only and nowhere else in the app.
 */
export default function MemberImport() {
  const [districts, setDistricts] = useState([]);
  const [districtId, setDistrictId] = useState("");
  const [fileError, setFileError] = useState("");
  const [rawRows, setRawRows] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");

  // Live gender sync from the BSA members feed (background job)
  const [syncDistrict, setSyncDistrict] = useState("");
  const [syncJobId, setSyncJobId] = useState(null);
  const [syncTask, setSyncTask] = useState(null);
  const [syncError, setSyncError] = useState("");

  useEffect(() => {
    if (!syncJobId) return undefined;
    if (syncTask && ["completed", "failed", "cancelled"].includes(syncTask.state)) {
      return undefined;
    }

    const interval = setInterval(async () => {
      try {
        const response = await membersService.getGenderSyncStatus(syncJobId);
        setSyncTask(response.data.task);
      } catch (err) {
        setSyncError(err.response?.data?.message || err.message || "Could not check sync status");
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [syncJobId, syncTask]);

  useEffect(() => {
    membersService
      .getBsaDistricts()
      .then((response) => setDistricts(response.data.districts || []))
      .catch(() => setDistricts([]));
  }, []);

  const selectedDistrict = districts.find((d) => d.districtId === districtId);

  const parseFile = (file) => {
    setFileError("");
    setPreview(null);
    setSummary(null);
    setError("");

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          setFileError(`Could not read the file: ${results.errors[0].message}`);
          return;
        }
        setRawRows(results.data);
      },
      error: (err) => setFileError(err.message || "Could not read the file"),
    });
  };

  const runPreview = async () => {
    setPreviewing(true);
    setError("");
    setSummary(null);

    try {
      const response = await membersService.previewMemberImport(
        rawRows,
        districtId,
        selectedDistrict?.districtName || districtId,
      );
      setPreview(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const runGenderSync = async () => {
    setSyncError("");
    setSyncTask(null);

    try {
      const response = await membersService.startGenderSync(syncDistrict);
      setSyncJobId(response.data.task.id);
      setSyncTask(response.data.task);
    } catch (err) {
      setSyncError(
        err.response?.data?.message || err.message || "Could not start sync",
      );
    }
  };

  const syncDone = syncTask && ["completed", "failed", "cancelled"].includes(syncTask.state);
  const syncResult = syncDone && syncTask.state === "completed" ? syncTask.result : null;

  const runImport = async () => {
    setImporting(true);
    setError("");
    setSummary(null);

    try {
      const response = await membersService.importMembers(
        rawRows,
        districtId,
        selectedDistrict?.districtName || districtId,
      );
      setSummary(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const clubLabel = (row) =>
    row.homeClubId
      ? `${row.homeClubRaw} ✓`
      : row.homeClubRaw
      ? `${row.homeClubRaw} — not found`
      : "—";

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Member Import
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload a BSA member export for a district to complete player profiles.
        Players are matched by BSA number: new players are created, existing
        players only gain fields that are still empty - nothing is overwritten.
      </Typography>

      <Stack spacing={2} sx={{ mb: 3 }}>
        <TextField
          select
          label="District"
          value={districtId}
          onChange={(event) => setDistrictId(event.target.value)}
          sx={{ maxWidth: 420 }}
          size="small"
        >
          {districts.map((district) => (
            <MenuItem key={district.districtId} value={district.districtId}>
              {district.districtName} ({district.districtId})
            </MenuItem>
          ))}
        </TextField>

        <Stack direction="row" spacing={2} alignItems="center">
          <Button variant="outlined" component="label">
            Choose CSV file
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) parseFile(file);
              }}
            />
          </Button>
          {rawRows && (
            <Typography variant="body2" color="text.secondary">
              {rawRows.length} rows loaded
            </Typography>
          )}
          <Button
            variant="contained"
            disabled={!rawRows || !districtId || previewing || importing}
            onClick={runPreview}
          >
            {previewing ? "Previewing..." : "Preview"}
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={!preview || importing || previewing}
            onClick={runImport}
          >
            {importing ? "Importing..." : "Import"}
          </Button>
        </Stack>

        {fileError && <Alert severity="error">{fileError}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Sync genders from BSA
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Live from the BSA members feed: updates existing players' gender and
          creates players that do not exist yet, for one district or the whole
          country.
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            select
            label="District"
            value={syncDistrict}
            onChange={(event) => setSyncDistrict(event.target.value)}
            sx={{ minWidth: 320 }}
            size="small"
          >
            <MenuItem value="">All districts</MenuItem>
            {districts.map((district) => (
              <MenuItem key={district.districtId} value={district.districtId}>
                {district.districtName} ({district.districtId})
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            color="success"
            disabled={Boolean(syncTask && !syncDone)}
            onClick={runGenderSync}
          >
            {syncTask && !syncDone ? "Syncing..." : "Sync now"}
          </Button>
        </Stack>

        {syncError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {syncError}
          </Alert>
        )}

        {syncTask && !syncDone && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Syncing in the background — {syncTask.state} ({syncTask.progress}%)
            </Typography>
            <LinearProgress
              variant="determinate"
              value={syncTask.progress || 0}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        )}

        {syncTask && syncTask.state === "failed" && (
          <Alert severity="error" sx={{ mt: 2 }}>
            The sync failed. Check the Task List under Admin Actions for details.
          </Alert>
        )}

        {syncResult && (
          <Alert severity="success" sx={{ mt: 2 }}>
            <strong>Sync complete:</strong> {syncResult.matched} members matched —{" "}
            {syncResult.created} created, {syncResult.updated} gender updates,{" "}
            {syncResult.unchanged} unchanged, {syncResult.skipped} skipped
            {syncResult.errors.length > 0 && `, ${syncResult.errors.length} errors`}
          </Alert>
        )}

        {syncResult && syncResult.errors.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {syncResult.errors.slice(0, 20).map((err, index) => (
              <Typography key={index} variant="body2" color="error">
                {err.bsa || err.row}: {err.reason}
              </Typography>
            ))}
          </Box>
        )}
      </Paper>

      {summary && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <strong>Import complete:</strong> {summary.created} created,{" "}
          {summary.updated} updated, {summary.unchanged} unchanged,{" "}
          {summary.skipped} skipped
          {summary.fallbackClubMatches > 0 &&
            `, ${summary.fallbackClubMatches} club(s) matched by name only`}
          {summary.errors.length > 0 && `, ${summary.errors.length} errors`}
        </Alert>
      )}

      {summary && summary.errors.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Errors
          </Typography>
          {summary.errors.slice(0, 50).map((err, index) => (
            <Typography key={index} variant="body2" color="error">
              Row {err.row}: {err.reason}
            </Typography>
          ))}
        </Paper>
      )}

      {preview && (
        <Paper variant="outlined">
          <Typography variant="subtitle2" sx={{ p: 2, pb: 0 }}>
            Preview — {preview.parsed} of {preview.total} rows parsed
            {preview.errors.length > 0 && `, ${preview.errors.length} errors`}
          </Typography>

          {preview.errors.length > 0 && (
            <Box sx={{ px: 2 }}>
              {preview.errors.slice(0, 20).map((err, index) => (
                <Typography key={index} variant="body2" color="error">
                  Row {err.row}: {err.reason}
                </Typography>
              ))}
            </Box>
          )}

          {preview.fallbackClubMatches.length > 0 && (
            <Alert severity="info" sx={{ m: 2 }}>
              Clubs matched by name only (different district or missing
              district id): {preview.fallbackClubMatches.join(", ")}
            </Alert>
          )}

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>BSA #</TableCell>
                  <TableCell>Birth date</TableCell>
                  <TableCell>Gender</TableCell>
                  <TableCell>Home club</TableCell>
                  <TableCell>Dual clubs</TableCell>
                  <TableCell>Race</TableCell>
                  <TableCell>Warnings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.rows.slice(0, 200).map((row) => (
                  <TableRow key={row.bsaNumber} hover>
                    <TableCell>{row.displayName}</TableCell>
                    <TableCell>{row.bsaNumber}</TableCell>
                    <TableCell>{row.dob || "—"}</TableCell>
                    <TableCell>{row.gender || "—"}</TableCell>
                    <TableCell>{clubLabel(row)}</TableCell>
                    <TableCell>
                      {row.dualClubIds.length > 0
                        ? `${row.dualClubIds.length} linked`
                        : row.dualClubsRaw.length > 0
                        ? "unmatched"
                        : "—"}
                    </TableCell>
                    <TableCell>{row.race || "—"}</TableCell>
                    <TableCell>
                      {row.warnings.length > 0
                        ? row.warnings.join("; ")
                        : ""}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {preview.rows.length > 200 && (
            <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: "block" }}>
              Showing the first 200 rows of {preview.rows.length}.
            </Typography>
          )}
        </Paper>
      )}

      {(previewing || importing) && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
}
